import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '@environments/environment';
import { ApiResponse } from '@core/types';
import {
  Invoice,
  InvoiceDetail,
  InvoiceCreate,
  InvoiceUpdate,
  InvoiceBatchCreate,
  InvoiceGapMonth,
  InvoiceBatchByMonth,
  InvoiceBatchByMonthResult,
} from '@core/types/finance.type';
import { PaginationResult, PaginationConfig } from './unit.service';
import { buildPaginationBody } from '@core/utils/pagination.util';

@Injectable({ providedIn: 'root' })
export class InvoiceService {
  private readonly http = inject(HttpClient);
  private readonly apiUrl = `${environment.api}/finance/invoices`;

  getInvoices(config: PaginationConfig): Observable<ApiResponse<PaginationResult<Invoice>>> {
    return this.http.post<ApiResponse<PaginationResult<Invoice>>>(`${this.apiUrl}/grid`, buildPaginationBody(config));
  }

  getInvoice(id: string): Observable<ApiResponse<InvoiceDetail>> {
    return this.http.get<ApiResponse<InvoiceDetail>>(`${this.apiUrl}/${id}`);
  }

  createInvoice(data: InvoiceCreate): Observable<ApiResponse<string>> {
    return this.http.post<ApiResponse<string>>(this.apiUrl, data);
  }

  createInvoiceBatch(data: InvoiceBatchCreate): Observable<ApiResponse<string[]>> {
    return this.http.post<ApiResponse<string[]>>(`${this.apiUrl}/batch`, data);
  }

  getInvoiceGaps(params?: {
    fromYear?: number;
    fromMonth?: number;
    toYear?: number;
    toMonth?: number;
  }): Observable<ApiResponse<InvoiceGapMonth[]>> {
    let httpParams = new HttpParams();
    if (params?.fromYear != null) httpParams = httpParams.set('fromYear', params.fromYear);
    if (params?.fromMonth != null) httpParams = httpParams.set('fromMonth', params.fromMonth);
    if (params?.toYear != null) httpParams = httpParams.set('toYear', params.toYear);
    if (params?.toMonth != null) httpParams = httpParams.set('toMonth', params.toMonth);
    return this.http.get<ApiResponse<InvoiceGapMonth[]>>(`${this.apiUrl}/gaps`, { params: httpParams });
  }

  createInvoiceBatchByMonth(data: InvoiceBatchByMonth): Observable<ApiResponse<InvoiceBatchByMonthResult>> {
    return this.http.post<ApiResponse<InvoiceBatchByMonthResult>>(`${this.apiUrl}/batch/by-month`, data);
  }

  updateInvoice(data: InvoiceUpdate): Observable<ApiResponse<void>> {
    return this.http.put<ApiResponse<void>>(this.apiUrl, data);
  }

  deleteInvoice(id: string): Observable<ApiResponse<void>> {
    return this.http.delete<ApiResponse<void>>(`${this.apiUrl}/${id}`);
  }

  cancelInvoice(id: string): Observable<ApiResponse<void>> {
    return this.http.post<ApiResponse<void>>(`${this.apiUrl}/${id}/cancel`, {});
  }

  sendInvoiceToResident(id: string): Observable<ApiResponse<void>> {
    return this.http.post<ApiResponse<void>>(`${this.apiUrl}/${id}/send-to-resident`, {});
  }
}
